import React, { useRef } from 'react';
import classNames from 'classnames';
import { CAROUSEL_MEDIA_ATTR } from '@components/carousel/carouselContract';
import { GridItemProps } from '@components/grid';
import { TileBackground, TileBackgroundProps } from '@components/tile/TileBackground';

import './Tile.scss';

export interface TileProps {
  children?: React.ReactNode;
  /** Turns on/off the grow effect for the Tile. */
  disableGrowEffect?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLDivElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
  /** Tab index for navigating with keyboard. Default 0. */
  tabIndex?: number;
  className?: string;
  /** If true, the component is disabled. Affects animation, focus, and cursor */
  disabled?: boolean;
  /** If true, the component has an outline and no interactions. */
  isActive?: boolean;
  /** Pass props to TileBackground component */
  bgProps?: TileBackgroundProps;
  /** Enable background image grow effect on hover */
  hasBackgroundGrowEffect?: boolean;
  /** Aspect ratio for the Tile. */
  aspectRatio?: GridItemProps['aspectRatio'] | '9 / 19.48'; // prominent tile
}

/**
 * Tile Component
 * @param disableGrowEffect - Turns on/off the grow effect for the Tile.
 * @param onClick - onClick callback for the Tile.
 * @param onFocus - onFocus callback for the Tile.
 * @param onBlur - onBlur callback for the Tile.
 * @param tabIndex - tab index for navigating with keyboard. Default 0.
 * @param children - Any React.ReactNode element.
 * @param className - Additional classnames to be included as customization.
 * @param disabled - If true, the component is disabled. Affects animation, focus, and cursor.
 * @param isActive - If true, the component has an outline and no interactions.
 * @param bgProps - Pass props to TileBackground component.
 * @param hasBackgroundGrowEffect - Enable background image grow effect on hover.
 * @param aspectRatio - Aspect ratio for the Tile.
 */
export const Tile: React.FC<TileProps> = ({
  children,
  disableGrowEffect = false,
  onClick,
  onFocus,
  onBlur,
  className,
  tabIndex = 0,
  disabled,
  isActive,
  bgProps,
  hasBackgroundGrowEffect,
  aspectRatio,
  ...rest
}) => {
  const tileRef = useRef<HTMLDivElement>(null);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ') {
      e.preventDefault();
      tileRef?.current?.click();
    }
  };

  const isDisabledOrActive = disabled || isActive;

  return (
    <div
      className={classNames('vds-tile', className, {
        'vds-tile--grow-effect': !disableGrowEffect && !isDisabledOrActive,
        'vds-tile--bg-grow-effect': hasBackgroundGrowEffect,
        'vds-tile--disabled': disabled,
        'vds-tile--active': isActive,
      })}
      onClick={!isDisabledOrActive ? onClick : undefined}
      onFocus={!isDisabledOrActive ? onFocus : undefined}
      onBlur={!isDisabledOrActive ? onBlur : undefined}
      onKeyDown={handleKeyDown}
      tabIndex={tabIndex}
      ref={tileRef}
      style={{ aspectRatio }}
      {...rest}
      {...{ [CAROUSEL_MEDIA_ATTR]: '' }}>
      {bgProps && (
        <TileBackground
          {...bgProps}
          className={classNames('vds-tile__background', bgProps.className)}
        />
      )}
      {children}
    </div>
  );
};
